import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppConfig } from "@context/AppConfigContext";
import { fetchClientsList } from "@actions/fetchClientsAction";
import { getDefaultClient, hasStoredClient } from "@utils/index";

export const useChangeClientData = () => {
  const navigate = useNavigate();
  const { selectedClient, updateSelectedClient } = useAppConfig();

  const [activeClients, setActiveClients] = useState([]);
  const [tempSelectedClient, setTempSelectedClient] = useState(
    selectedClient?.clientId || getDefaultClient().clientId,
  );
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch active clients on mount - single API call
  useEffect(() => {
    const fetchActiveClientsData = async () => {
      setLoading(true);
      try {
        const result = await fetchClientsList("active");

        if (result.success && result.data && result.data.length > 0) {
          setActiveClients(result.data);

          // If no client is set in localStorage (first time after login), default to configured client
          if (!hasStoredClient()) {
            const defaultClient = getDefaultClient();
            updateSelectedClient(
              defaultClient.clientId,
              defaultClient.clientName,
            );
            setTempSelectedClient(defaultClient.clientId);
          }
        } else {
          toast.error("No active clients found");
          // Still set default even if API returns empty
          if (!hasStoredClient()) {
            const defaultClient = getDefaultClient();
            updateSelectedClient(
              defaultClient.clientId,
              defaultClient.clientName,
            );
            setTempSelectedClient(defaultClient.clientId);
          }
        }
      } catch (error) {
        console.error("Error fetching active clients:", error);
        toast.error("Failed to load clients. Please try again.");

        // On API failure, ensure default is set if no client exists
        if (!hasStoredClient()) {
          const defaultClient = getDefaultClient();
          updateSelectedClient(
            defaultClient.clientId,
            defaultClient.clientName,
          );
          setTempSelectedClient(defaultClient.clientId);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchActiveClientsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Sync temp selection with context when selectedClient changes
  useEffect(() => {
    if (selectedClient?.clientId) {
      setTempSelectedClient(selectedClient.clientId);
    }
  }, [selectedClient]);

  // Transform clients to dropdown options
  const clientOptions = [
    ...(activeClients?.map((client) => ({
      value: client.client_id,
      label: client.client_name,
    })) || []),
  ];

  // Remove duplicate EMT if it exists in the API response
  const uniqueClientOptions = clientOptions.filter(
    (option, index, self) =>
      index === self.findIndex((t) => t.value === option.value),
  );

  const handleClientChange = useCallback((clientId) => {
    setTempSelectedClient(clientId);
  }, []);

  const getCurrentClientName = useCallback(() => {
    return selectedClient?.clientName || getDefaultClient().clientName;
  }, [selectedClient]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (tempSelectedClient === selectedClient?.clientId) {
        toast.info("Client is already selected");
        return;
      }

      setIsSubmitting(true);

      try {
        // Find the selected client name
        const selectedClientData = uniqueClientOptions.find(
          (opt) => opt.value === tempSelectedClient,
        );
        const clientName = selectedClientData?.label || tempSelectedClient;

        // Update the selected client in context and localStorage
        updateSelectedClient(tempSelectedClient, clientName);

        toast.success(`Client changed to ${clientName} successfully!`);

        setTimeout(() => {
          navigate("/dashboard");
        }, [1000]);

        // Reload the page to fetch new data for selected client
      } catch (error) {
        console.error("Error changing client:", error);
        toast.error("Failed to change client. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      tempSelectedClient,
      selectedClient?.clientId,
      uniqueClientOptions,
      updateSelectedClient,
      navigate,
    ],
  );

  const handleCancel = useCallback(() => {
    navigate(-1); // Go back to previous page
  }, [navigate]);

  return {
    clientOptions: uniqueClientOptions,
    selectedClient,
    tempSelectedClient,
    loading,
    isSubmitting,
    handleClientChange,
    handleSubmit,
    handleCancel,
    getCurrentClientName,
  };
};
